import { Fancybox } from '@fancyapps/ui/dist/fancybox/';
import Splide from '@splidejs/splide';
import { unitlessBreakpoints } from '../../helpers';

const selectors = {
  productImage: '[data-product-image]',
  mainCarousel: '[data-image-carousel]',
  thumbnailCarousel: '[data-thumbnail-carousel]',
  progressBar: '[data-progress-bar]',
  currentIndex: '[data-carousel--current-index]',
  totalIndex: '[data-carousel--total-index]',
  openFancybox: '[data-open-fancybox]',
  fancyboxSlideTrigger: '[data-fancybox-slide-trigger]',
  mediaCredit: '[data-media-credit]',
};

/**
 * Product image gallery on the PDP
 *
 * @export
 * @class ProductImages
 */
export default class ProductImages {
  constructor(el) {
    this.el = el;
    this.mainCarouselEl = el.querySelector(selectors.mainCarousel);
    this.thumbnailCarouselEl = el.querySelector(selectors.thumbnailCarousel);
    this.navItems = this.thumbnailCarouselEl.querySelectorAll(
      selectors.productImage,
    );
    this.openFancyboxBtn = el.querySelector(selectors.openFancybox);
    this._initCarousel();
    this._initFancybox();
  }

  /**
   * Initialize primary image carousel
   */
  _initCarousel() {
    this.mainCarousel = new Splide(this.mainCarouselEl, {
      perPage: 1,
      pagination: false,
      arrows: false,
      mediaQuery: 'min',
      speed: 600,
    });

    this.thumbnailCarousel = new Splide(this.thumbnailCarouselEl, {
      destroy: true,
      fixedWidth: 90,
      fixedHeight: 'auto',
      gap: 6,
      pagination: false,
      arrows: true,
      isNavigation: true,
      wheel: true,
      wheelSleep: 600,
      speed: 600,
      waitForTransition: true,
      mediaQuery: 'min',
      breakpoints: {
        [unitlessBreakpoints.lg]: {
          destroy: false,
          direction: 'ttb',
          height: 'auto',
        },
      },
    });

    const progressBar = this.mainCarousel.root.querySelector(
      selectors.progressBar,
    );
    const currentIndexEl = this.mainCarousel.root.querySelector(
      selectors.currentIndex,
    );
    const totalIndexEl = this.mainCarousel.root.querySelector(
      selectors.totalIndex,
    );
    this.mainCarousel.on('mounted move', () => {
      const end = this.mainCarousel.Components.Controller.getEnd() + 1;
      const current = this.mainCarousel.index + 1;
      const rate = Math.min(current / end, 1);
      progressBar.style.transform = `scaleX(${rate})`;
      currentIndexEl.textContent = current;
      totalIndexEl.textContent = end;
    });

    this.mainCarousel.sync(this.thumbnailCarousel);
    this.mainCarousel.mount();
    this.thumbnailCarousel.mount();
    this._stripListOrientation();
  }

  /**
   * Splide's isNavigation sync sets aria-orientation on the thumbnail list,
   * which the list role doesn't allow (axe aria-allowed-attr). Observe rather
   * than hook events: Splide's destroy/remount at the lg breakpoint wipes
   * handlers registered via splide.on().
   */
  _stripListOrientation() {
    const list = this.thumbnailCarouselEl.querySelector('.splide__list');
    if (!list) return;

    const strip = () => list.removeAttribute('aria-orientation');
    strip();
    new MutationObserver(strip).observe(list, {
      attributeFilter: ['aria-orientation'],
    });
  }

  /**
   * Initialize Fancybox click handler
   */
  _initFancybox() {
    if (this.openFancyboxBtn) {
      this.openFancyboxBtn.addEventListener('click', () => {
        this._openFancybox();
      });
    }

    this.mainCarouselEl.addEventListener('click', event => {
      // Let model credit product links navigate instead of opening zoom
      if (event.target.closest(selectors.mediaCredit)) {
        return;
      }

      const slide = event.target.closest(selectors.fancyboxSlideTrigger);
      if (!slide || !this.mainCarouselEl.contains(slide)) {
        return;
      }

      this._openFancybox();
    });
  }

  /**
   * Build Fancybox array from carousel slides and open Fancybox
   */
  _openFancybox() {
    const slides = this.mainCarouselEl.querySelectorAll('.splide__slide');
    const thumbnailSlides =
      this.thumbnailCarouselEl.querySelectorAll('.splide__slide');
    const fancyboxItems = [];

    slides.forEach((slide, index) => {
      const img = slide.querySelector('img');
      const video = slide.querySelector('video');

      // Handle regular video tags first
      if (video) {
        // Check for source elements first (Shopify video_tag creates these)
        const source = video.querySelector('source');
        let videoSrc = '';

        if (source) {
          videoSrc = source.src || source.getAttribute('src') || '';
        } else {
          // Fallback to video src attribute
          videoSrc = video.src || '';
        }

        if (!videoSrc) {
          return;
        }

        // Get thumbnail from corresponding thumbnail slide
        let thumbSrc = '';
        if (thumbnailSlides[index]) {
          const thumbImg = thumbnailSlides[index].querySelector('img');
          if (thumbImg) {
            thumbSrc = thumbImg.src || thumbImg.dataset.src || '';
          }
        }

        fancyboxItems.push({
          src: videoSrc,
          thumbSrc: thumbSrc,
          caption: video.getAttribute('alt') || '',
        });
        return; // Skip to next slide
      }
      // Handle images
      if (img) {
        // Get full-size image src
        // Check for srcset first, then src, then data-src
        let src = img.src || img.dataset.src || '';

        // If srcset exists, get the largest image (last in srcset)
        if (img.srcset) {
          const srcsetParts = img.srcset.split(',');
          const largest = srcsetParts[srcsetParts.length - 1]
            .trim()
            .split(' ')[0];

          src = largest || src;
        }

        // Get thumbnail src
        let thumbSrc = '';
        if (thumbnailSlides[index]) {
          const thumbImg = thumbnailSlides[index].querySelector('img');
          if (thumbImg) {
            thumbSrc = thumbImg.src || thumbImg.dataset.src || '';
          }
        }

        // Model credit HTML from Liquid, falling back to alt text
        const caption = slide.dataset.caption || img.alt || '';

        fancyboxItems.push({
          src: src,
          thumbSrc: thumbSrc,
          caption: caption,
        });
      }
    });

    if (fancyboxItems.length === 0) {
      return;
    }
    // Open Fancybox starting at current slide
    Fancybox.show(fancyboxItems, {
      startIndex: this.mainCarousel.index,
    });
  }

  /**
   * Handler for swapping images when the variant changes
   *
   * @param {Object} variant The currently selected variant
   * @public
   */
  change(variant) {
    this.navItems.forEach((element, index) => {
      const { imageVariants } = element.dataset;
      // Bail if array of variants for this image
      // doesn't contain currently selected variant id
      if (!imageVariants.split(',').includes(String(variant.id))) {
        return;
      }

      this.mainCarousel.go(index);
    });
  }
}
